import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Check, CalendarPlus, Download, Share2, Video, Mail, HelpCircle, ExternalLink, Copy, AlertTriangle, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateGoogleCalendarUrl, downloadICS } from "@/lib/calendar";
import { ShareSheet } from "@/components/ShareSheet";
import { EventImage } from "@/components/EventImage";
import { TicketDownloadCard } from "./TicketDownloadCard";
import { formatBRL } from "@/lib/utils";
import { resolveTicketQrCode } from "@/lib/ticket-qr";
import { formatEventDateTimeRange, resolveEventTimeZone } from "@/lib/event-timezone";
import { ensureAbsoluteUrl, getEventPublicPath, getLiveRedirectUrl } from "@/lib/urls";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

// ── Animated SVGs for Confirmation Step ──
function AnimatedPaperPlane() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes openFlap {
            0%, 15% { transform: scaleY(1); }
            30%, 85% { transform: scaleY(-1); }
            95%, 100% { transform: scaleY(1); }
          }
          @keyframes launchPlane {
            0%, 25% { transform: translate(-5px, 8px) scale(0.6) rotate(15deg); opacity: 0; }
            40% { opacity: 1; }
            75% { transform: translate(25px, -25px) scale(1.1) rotate(-10deg); opacity: 0; }
            80%, 100% { opacity: 0; }
          }
          @keyframes floatEnvelope {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          @keyframes sparkle {
            0%, 100% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1); opacity: 1; }
          }
          .flap-anim {
            transform-origin: 40px 48px;
            animation: openFlap 4s infinite ease-in-out;
          }
          .plane-anim {
            transform-origin: 40px 45px;
            animation: launchPlane 4s infinite ease-in-out;
          }
          .envelope-group {
            animation: floatEnvelope 4s infinite ease-in-out;
          }
        `}</style>
        <defs>
          <linearGradient id="brandOrange" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
          <linearGradient id="envelopeBg" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffebe8" />
            <stop offset="100%" stopColor="#ffd8d3" />
          </linearGradient>
        </defs>

        {/* Sparkles */}
        <g transform="translate(58, 18)">
          <path d="M 0,-3 Q 0,0 3,0 Q 0,0 0,3 Q 0,0 -3,0 Q 0,0 0,-3" fill="hsl(var(--primary))" className="scale-0" style={{ animation: "sparkle 2s infinite ease-in-out" }} />
        </g>
        <g transform="translate(18, 28)">
          <path d="M 0,-2.5 Q 0,0 2.5,0 Q 0,0 0,2.5 Q 0,0 -2.5,0 Q 0,0 0,-2.5" fill="#ff826e" className="scale-0" style={{ animation: "sparkle 2.5s infinite ease-in-out 0.5s" }} />
        </g>

        {/* Main Envelope Group */}
        <g className="envelope-group" transform="translate(0, 4)">
          {/* Envelope Back */}
          <rect x="20" y="38" width="40" height="26" rx="4" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          
          {/* Letter Paper peaking out */}
          <rect x="24" y="30" width="32" height="20" rx="2" fill="white" stroke="#ffe0db" strokeWidth="1.2">
            <line x1="28" y1="36" x2="52" y2="36" stroke="#ffd8d3" strokeWidth="1" />
            <line x1="28" y1="40" x2="48" y2="40" stroke="#ffd8d3" strokeWidth="1" />
            <line x1="28" y1="44" x2="44" y2="44" stroke="#ffd8d3" strokeWidth="1" />
          </rect>

          {/* Flying Paper Plane */}
          <g className="plane-anim">
            <path d="M 40,40 L 48,28 L 32,34 Z" fill="url(#brandOrange)" />
            <path d="M 40,40 L 48,28 L 39,36 Z" fill="#d33c20" />
            <path d="M 39,36 L 40,40 L 41,36 Z" fill="#a72812" />
          </g>

          {/* Envelope Front Flaps */}
          <path d="M 20,38 L 40,50 L 20,64 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M 60,38 L 40,50 L 60,64 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M 20,64 L 40,48 L 60,64 Z" fill="#fff5f4" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
          <path className="flap-anim" d="M 20,38 L 40,48 L 60,38 Z" fill="#fff5f4" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeLinejoin="round" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedTransmission() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes floatMobile {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          @keyframes floatHeart {
            0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
            15% { opacity: 0.8; }
            80% { opacity: 0.8; }
            100% { transform: translate(var(--x-dir), -35px) scale(1); opacity: 0; }
          }
          @keyframes pulsePlay {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.15); }
          }
          .phone-container {
            animation: floatMobile 4s infinite ease-in-out;
            transform-origin: 40px 40px;
          }
          .heart {
            transform-origin: 40px 45px;
            animation: floatHeart 3s infinite linear;
          }
          .play-btn {
            transform-origin: 40px 40px;
            animation: pulsePlay 2s infinite ease-in-out;
          }
        `}</style>
        <defs>
          <linearGradient id="phoneScreenGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fff5f4" />
            <stop offset="100%" stopColor="#fff" />
          </linearGradient>
          <linearGradient id="primaryOrange" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
        </defs>

        {/* Floating Reactions/Hearts */}
        <g className="heart" style={{ "--x-dir": "12px", animationDelay: "0s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -3,-3 -6,-1 -6,2 C -6,5 0,9 0,9 C 0,9 6,5 6,2 C 6,-1 3,-3 0,0 Z" fill="hsl(var(--primary))" />
        </g>
        <g className="heart" style={{ "--x-dir": "-15px", animationDelay: "1s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -2.5,-2.5 -5,-1 -5,1.5 C -5,4 0,7 0,7 C 0,7 5,4 5,1.5 C 5,-1 2.5,-2.5 0,0 Z" fill="#ff826e" />
        </g>
        <g className="heart" style={{ "--x-dir": "5px", animationDelay: "2s" } as any} transform="translate(40, 42)">
          <path d="M 0,0 C -2,-2 -4,-0.8 -4,1.2 C -4,3.2 0,5.6 0,5.6 C 0,5.6 4,3.2 4,1.2 C 4,-0.8 2,-2 0,0 Z" fill="#ffbda8" />
        </g>

        {/* Mobile Phone Screen Group */}
        <g className="phone-container" transform="translate(0, 2)">
          {/* Phone Shell */}
          <rect x="26" y="16" width="28" height="48" rx="5" fill="white" stroke="hsl(var(--primary))" strokeWidth="2" />
          {/* Phone Screen Area */}
          <rect x="29" y="20" width="22" height="40" rx="2" fill="url(#phoneScreenGrad)" />
          {/* Speaker */}
          <line x1="37" y1="18" x2="43" y2="18" stroke="#ffe0db" strokeWidth="1" strokeLinecap="round" />
          
          {/* Pulsing play button */}
          <g className="play-btn">
            <circle cx="40" cy="40" r="7" fill="url(#primaryOrange)" />
            <polygon points="38.5,37 43.5,40 38.5,43" fill="white" />
          </g>

          {/* Micro elements at bottom of phone screen */}
          <rect x="33" y="52" width="14" height="2" rx="0.5" fill="#ffd8d3" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedMapPin() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes bouncePin {
            0%, 100% { transform: translateY(0) scaleY(1); }
            50% { transform: translateY(-5px) scaleY(0.96); }
          }
          @keyframes sonarWave {
            0% { transform: scale(0.4); opacity: 0.8; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          .map-pin {
            transform-origin: 40px 52px;
            animation: bouncePin 2s infinite ease-in-out;
          }
          .sonar-ring {
            transform-origin: 40px 52px;
            animation: sonarWave 2s infinite ease-out;
          }
        `}</style>
        <defs>
          <linearGradient id="mapPinGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ff826e" />
            <stop offset="100%" stopColor="hsl(var(--primary))" />
          </linearGradient>
        </defs>

        {/* Map sonar rings */}
        <ellipse cx="40" cy="52" rx="14" ry="4" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" className="sonar-ring" style={{ animationDelay: "0s" }} />
        <ellipse cx="40" cy="52" rx="14" ry="4" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" className="sonar-ring" style={{ animationDelay: "1s" }} />
        <ellipse cx="40" cy="52" rx="5" ry="1.5" fill="hsl(var(--primary))" opacity="0.3" />

        {/* Folded Map Base */}
        <path d="M 22,46 L 34,42 L 46,46 L 58,42 L 58,62 L 46,66 L 34,62 L 22,66 Z" fill="white" stroke="#ffe0db" strokeWidth="1.5" strokeLinejoin="round" />
        {/* Map roads/routes */}
        <path d="M 25,56 L 34,52 L 46,56 L 55,52" fill="none" stroke="#ffd8d3" strokeWidth="2" strokeLinecap="round" />
        <path d="M 34,42 L 34,62" fill="none" stroke="#ffd8d3" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M 46,46 L 46,66" fill="none" stroke="#ffd8d3" strokeWidth="1.5" strokeLinecap="round" />

        {/* Map Pin */}
        <g className="map-pin">
          <path d="M 40,52 C 32,44 26,37 26,27 A 14,14 0 0 1 54,27 C 54,37 48,44 40,52 Z" fill="url(#mapPinGrad)" stroke="#c33015" strokeWidth="1" strokeLinejoin="round" />
          <circle cx="40" cy="27" r="5" fill="white" />
        </g>
      </svg>
    </div>
  );
}

function AnimatedSupport() {
  return (
    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
      <svg viewBox="0 0 80 80" className="w-16 h-16">
        <style>{`
          @keyframes bubbleLeft {
            0%, 5% { transform: scale(0); opacity: 0; }
            10%, 45% { transform: scale(1); opacity: 1; }
            50%, 100% { transform: scale(0); opacity: 0; }
          }
          @keyframes bubbleRight {
            0%, 50% { transform: scale(0); opacity: 0; }
            55%, 90% { transform: scale(1); opacity: 1; }
            95%, 100% { transform: scale(0); opacity: 0; }
          }
          @keyframes bounceDot {
            0%, 100% { transform: translateY(0); opacity: 0.3; }
            50% { transform: translateY(-2px); opacity: 1; }
          }
          .bubble-l {
            transform-origin: 22px 50px;
            animation: bubbleLeft 6s infinite cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .bubble-r {
            transform-origin: 58px 38px;
            animation: bubbleRight 6s infinite cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .dot-1 { animation: bounceDot 1.2s infinite ease-in-out; }
          .dot-2 { animation: bounceDot 1.2s infinite ease-in-out 0.2s; }
          .dot-3 { animation: bounceDot 1.2s infinite ease-in-out 0.4s; }
        `}</style>
        <defs>
          <linearGradient id="supportOrangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="#ff826e" />
          </linearGradient>
        </defs>

        {/* Background rings */}
        <circle cx="40" cy="40" r="30" stroke="#ffebe8" strokeWidth="1" strokeDasharray="3 6" fill="none" />

        {/* Left Chat Bubble */}
        <g className="bubble-l">
          <path d="M 20,50 L 20,44 A 6,6 0 0 1 26,38 L 44,38 A 6,6 0 0 1 50,44 L 50,50 A 6,6 0 0 1 44,56 L 24,56 L 18,60 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <circle cx="28" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-1" />
          <circle cx="34" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-2" />
          <circle cx="40" cy="47" r="1.5" fill="hsl(var(--primary))" className="dot-3" />
        </g>

        {/* Right Chat Bubble */}
        <g className="bubble-r">
          <path d="M 36,26 L 54,26 A 6,6 0 0 1 60,32 L 60,38 A 6,6 0 0 1 54,44 L 36,44 A 6,6 0 0 1 30,38 L 30,32 A 6,6 0 0 1 36,26 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <path d="M 54,44 L 62,48 L 58,40 Z" fill="white" stroke="hsl(var(--primary))" strokeWidth="1.5" />
          <text x="45" y="37" fill="hsl(var(--primary))" fontSize="9" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle">?</text>
        </g>
      </svg>
    </div>
  );
}

interface CheckoutStepConfirmationProps {
  orderId?: string | null;
  boletoUrl?: string | null;
  boletoBarcode?: string | null;
}

export function CheckoutStepConfirmation({ orderId, boletoUrl, boletoBarcode }: CheckoutStepConfirmationProps) {
  const { t } = useTranslation("checkout");
  useEffect(() => {
    const duration = 5 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) =>
      Math.random() * (max - min) + min;

    const interval = window.setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  const { data: order } = useQuery({
    queryKey: ["order-confirmation", orderId],
    queryFn: async () => {
      const { data } = await supabase
        .from("orders")
        .select("*, events(title, start_date, end_date, venue_name, venue_address, venue_city, cover_image_url, slug, is_online, online_url, streaming_redirect_token, description, producer_id, organization_id, profiles!events_producer_id_fkey(organizer_slug), organizations(slug))")
        .eq("id", orderId!)
        .single();
      return data;
    },
    enabled: !!orderId,
  });

  const { data: tickets } = useQuery({
    queryKey: ["order-tickets", orderId],
    queryFn: async () => {
      const { data } = await supabase
        .from("tickets")
        .select("id, qr_code, qr_code_image_url, attendee_name, attendee_cpf, ticket_tiers(name, price)")
        .eq("order_id", orderId!);
      return data;
    },
    enabled: !!orderId,
  });

  const event = order?.events as any;
  const eventTimeZone = resolveEventTimeZone(event?.time_zone);

  const handleAddToCalendar = (type: "google" | "ics") => {
    if (!event) return;
    const location = [event.venue_name, event.venue_address, event.venue_city].filter(Boolean).join(", ");
    const params = {
      title: event.title,
      startDate: event.start_date,
      endDate: event.end_date,
      location,
      description: `Ingresso adquirido via TicketHall. Pedido #${orderId?.slice(0, 8).toUpperCase()}`,
    };
    if (type === "google") {
      window.open(generateGoogleCalendarUrl(params), "_blank");
    } else {
      downloadICS(params);
    }
  };

  const isOrg = !!event?.organization_id && !!event?.organizations?.slug;
  const producerSlug = isOrg ? event?.organizations?.slug : event?.profiles?.organizer_slug;
  const orderCode = orderId?.slice(0, 8).toUpperCase() || "";
  const eventUrl = event?.slug ? `${window.location.origin}${getEventPublicPath(event.slug, producerSlug, isOrg)}` : "";

  return (
    <div className="space-y-8 py-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-5">
        <div className="flex justify-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/40 flex items-center justify-center animate-in scale-in-50 duration-500">
            <Check className="h-12 w-12 text-primary" strokeWidth={1.5} />
          </div>
        </div>
        <div className="space-y-3">
          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">{t("checkout.confirmation.orderSuccess")}</h2>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm">{t("checkout.confirmation.ticketsSentEmail")}</span>
          </div>
        </div>
        {orderCode && (
          <div className="pt-2 flex items-center justify-center gap-3">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("checkout.confirmation.orderNumber")}</span>
            <span className="font-mono font-bold text-foreground text-lg">{orderCode}</span>
          </div>
        )}
      </div>

      {/* Boleto alert — MUST PAY */}
      {boletoUrl && (
        <div className="p-5 rounded-xl border border-primary/30 bg-primary/5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">{t("checkout.confirmation.boletoPending")}</p>
              <p className="text-xs text-muted-foreground">
                {t("checkout.confirmation.boletoPendingNotice")}
              </p>
            </div>
          </div>

          <Button className="w-full gap-2" onClick={() => window.open(boletoUrl, "_blank")}>
            <ExternalLink className="h-4 w-4" />
            {t("checkout.confirmation.payBoletoPdf")}
          </Button>

          {boletoBarcode && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t("checkout.confirmation.barcode")}</p>
              <div className="flex items-center gap-2">
                <input
                  value={boletoBarcode}
                  readOnly
                  className="flex-1 px-3 py-2 rounded-md border border-primary/20 bg-white dark:bg-background text-xs font-mono focus:outline-none"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(boletoBarcode);
                    toast({ title: t("checkout.toastsAndErrors.codeCopied") });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {order && event && (
        <div className="space-y-6">
          {/* Event summary card */}
          <div className="p-5 rounded-xl border border-border bg-card">
            <div className="flex flex-col sm:flex-row gap-4">
              <EventImage
                src={event.cover_image_url}
                alt={event.title}
                className="w-full sm:w-40 h-28 sm:h-24 rounded-lg object-cover shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-1.5">
                <p className="font-display font-semibold text-foreground text-lg leading-snug">{event.title}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <CalendarPlus className="h-3.5 w-3.5 shrink-0" />
                  {formatEventDateTimeRange(event.start_date, event.end_date, eventTimeZone, { dateStyle: "short" })}
                </p>
                {event.is_online ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Video className="h-3.5 w-3.5 shrink-0" />
                    {t("checkout.confirmation.onlineEvent")}
                  </p>
                ) : event.venue_name ? (
                  <p className="text-sm text-muted-foreground">
                    {event.venue_name}{event.venue_city ? `, ${event.venue_city}` : ""}
                  </p>
                ) : null}
              </div>
              {event.is_online && (
                <div className="sm:self-center shrink-0">
                  <Button asChild className="gap-2 w-full sm:w-auto">
                    <a
                      href={event.streaming_redirect_token ? getLiveRedirectUrl(event.streaming_redirect_token) : ensureAbsoluteUrl(event.online_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("checkout.confirmation.accessStream")}
                    </a>
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
              <ShareSheet url={eventUrl} title={event.title}>
                <Button variant="outline" size="sm" className="gap-2">
                  <Share2 className="h-4 w-4" />
                  {t("checkout.confirmation.share")}
                </Button>
              </ShareSheet>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-primary hover:text-primary"
                onClick={() => handleAddToCalendar("google")}
              >
                <CalendarPlus className="h-4 w-4" />
                {t("checkout.confirmation.addToCalendar")}
              </Button>
            </div>
          </div>

          {/* Ticket cards with QR codes */}
          {tickets && tickets.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("checkout.confirmation.yourTickets", { count: tickets.length })}
              </p>
              <div className="grid grid-cols-1 gap-6">
                {tickets.map((t: any, idx: number) => (
                  <TicketDownloadCard
                    key={t.id}
                    ticketId={t.id}
                    tierName={(t.ticket_tiers as any)?.name || "Ingresso"}
                    tierPrice={(t.ticket_tiers as any)?.price}
                    attendeeName={t.attendee_name}
                    attendeeCpf={t.attendee_cpf}
                    qrCode={resolveTicketQrCode(t.qr_code, t.id)}
                    qrCodeImageUrl={t.qr_code_image_url}
                    eventTitle={event.title}
                    eventDate={event.start_date}
                    eventEndDate={event.end_date}
                    eventTimeZone={eventTimeZone}
                    venueName={event.venue_name}
                    venueAddress={event.venue_address}
                    venueCity={event.venue_city}
                    isOnline={event.is_online}
                    coverImageUrl={event.cover_image_url}
                    orderCode={orderCode}
                    purchaseDate={order.created_at}
                    paymentMethod={order.payment_method}
                    eventDescription={event.description}
                    index={idx}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 3 Animated cards for user instructions */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("checkout.confirmation.importantInfo")}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Card 1: Ingressos enviados */}
              <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
                <AnimatedPaperPlane />
                <div className="mt-4 space-y-1">
                  <h4 className="text-sm font-semibold text-foreground">{t("checkout.confirmation.ticketsSentTitle")}</h4>
                  <p className="text-xs leading-relaxed text-muted-foreground m-0">
                    {t("checkout.confirmation.ticketsSentDesc", { email: order.buyer_email || "-" })}
                  </p>
                </div>
              </div>

              {/* Card 2: Link da transmissão ou Local do evento */}
              {event.is_online ? (
                <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
                  <AnimatedTransmission />
                  <div className="mt-4 space-y-1">
                    <h4 className="text-sm font-semibold text-foreground">{t("checkout.confirmation.streamLinkTitle")}</h4>
                    <p className="text-xs leading-relaxed text-muted-foreground m-0">
                      {event.streaming_redirect_token || event.online_url ? (
                        <>
                          {t("checkout.confirmation.streamLinkAvailable")}{" "}
                          <a
                            href={event.streaming_redirect_token ? getLiveRedirectUrl(event.streaming_redirect_token) : ensureAbsoluteUrl(event.online_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary font-semibold hover:underline inline-flex items-center gap-0.5"
                          >
                            {t("checkout.confirmation.accessNow")} <ExternalLink className="h-3 w-3 inline" />
                          </a>
                        </>
                      ) : (
                        t("checkout.confirmation.streamLinkPending")
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
                  <AnimatedMapPin />
                  <div className="mt-4 space-y-1">
                    <h4 className="text-sm font-semibold text-foreground">{t("checkout.confirmation.eventLocationTitle")}</h4>
                    <p className="text-xs leading-relaxed text-muted-foreground m-0">
                      {event.venue_name ? (
                        t("checkout.confirmation.eventLocationDesc", { venue: event.venue_name + (event.venue_address ? ` (${event.venue_address})` : "") })
                      ) : (
                        t("checkout.confirmation.eventLocationFallback")
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Card 3: Precisa de ajuda? */}
              <div className="flex flex-col items-center text-center p-6 rounded-2xl bg-white dark:bg-card border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-300">
                <AnimatedSupport />
                <div className="mt-4 space-y-1">
                  <h4 className="text-sm font-semibold text-foreground">{t("checkout.confirmation.needHelpTitle")}</h4>
                  <p className="text-xs leading-relaxed text-muted-foreground m-0">
                    {t("checkout.confirmation.needHelpDesc").split(t("checkout.confirmation.helpCenter"))[0]}
                    <Link to="/faq" className="text-primary font-semibold hover:underline">{t("checkout.confirmation.helpCenter")}</Link>
                    {t("checkout.confirmation.needHelpDesc").split(t("checkout.confirmation.helpCenter"))[1]}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Informações de Faturamento & Pagador */}
          <div className="rounded-2xl border border-border bg-white dark:bg-card p-6 text-left space-y-4 shadow-sm">
            <h3 className="font-display text-sm font-semibold text-foreground flex items-center gap-1.5 pb-2 border-b border-border m-0">
              <User className="h-4 w-4 text-primary" /> {t("checkout.confirmation.billingPayerInfo")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
              {/* Col 1: Dados do Pagador */}
              <div className="space-y-2.5">
                <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">{t("checkout.confirmation.payerDataHeader")}</span>
                <div className="space-y-1.5 text-foreground">
                  {order.billing_company_name ? (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.companyName")}</span> <span className="font-semibold">{order.billing_company_name}</span>
                    </div>
                  ) : (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.name")}</span> <span className="font-semibold">{order.buyer_name}</span>
                    </div>
                  )}
                  {order.buyer_document && (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.document")}</span> <span className="font-semibold font-mono">{order.buyer_document}</span>
                    </div>
                  )}
                  {order.buyer_email && (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.email")}</span> <span className="font-semibold">{order.buyer_email}</span>
                    </div>
                  )}
                  {order.buyer_phone && (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.phone")}</span> <span className="font-semibold font-mono">{order.buyer_phone}</span>
                    </div>
                  )}
                  {order.billing_address && (
                    <div>
                      <span className="text-muted-foreground font-normal">{t("checkout.confirmation.billingAddress")}</span>{" "}
                      <span className="font-semibold leading-relaxed">
                        {order.billing_address}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Col 2: Detalhes do Pagamento */}
              <div className="space-y-2.5">
                <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">{t("checkout.confirmation.paymentDetailsHeader")}</span>
                <div className="space-y-1.5 text-foreground">
                  <div>
                    <span className="text-muted-foreground font-normal">{t("checkout.confirmation.paymentMethod")}</span>{" "}
                    <span className="font-semibold">
                      {order.payment_method === "pix" && t("checkout.confirmation.pix")}
                      {order.payment_method === "boleto" && t("checkout.confirmation.boleto")}
                      {order.payment_method === "credit_card" && t("checkout.confirmation.creditCard")}
                      {order.payment_method === "free" && t("checkout.confirmation.free")}
                    </span>
                  </div>
                  {order.payment_method === "boleto" && order.billing_company_name && (
                    <div>
                      <span className="text-muted-foreground font-normal">Boleto emitido para CNPJ:</span> <span className="font-semibold font-mono">{order.buyer_document}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground font-normal">Status do pagamento:</span>{" "}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      order.payment_method === "free" || order.status === "paid"
                        ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"
                        : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary"
                    }`}>
                      {order.payment_method === "free" || order.status === "paid" ? "Aprovado" : "Aguardando pagamento"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-normal">Total da compra:</span>{" "}
                    <span className="font-semibold text-primary">
                      {formatBRL(order.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment summary */}
          <div className="p-4 rounded-xl border border-border bg-card space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cart.subtotal")}</span>
              <span>{formatBRL(order.subtotal)}</span>
            </div>
            {order.platform_fee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cart.serviceFee")}</span>
                <span>{formatBRL(order.platform_fee)}</span>
              </div>
            )}
            {(order.discount_amount ?? 0) > 0 && (
              <div className="flex justify-between text-primary">
                <span>{t("cart.discount")}</span>
                <span>-{formatBRL(order.discount_amount!)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t border-border pt-2">
              <span>{t("cart.total")}</span>
              <span>{formatBRL(order.total)}</span>
            </div>
          </div>

          {/* Calendar buttons */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => handleAddToCalendar("google")}>
              <CalendarPlus className="h-4 w-4" /> Google Calendar
            </Button>
            <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => handleAddToCalendar("ics")}>
              <Download className="h-4 w-4" /> Apple / Outlook
            </Button>
          </div>
        </div>
      )}

      <p className="text-muted-foreground max-w-md mx-auto text-center">
        {t("checkout.toastsAndErrors.ticketsGeneratedSuccess")}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button asChild>
          <Link to="/meus-ingressos">{t("checkout.confirmation.viewMyTickets")}</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/eventos">{t("cart.viewEvents")}</Link>
        </Button>
      </div>
    </div>
  );
}
